import React, { useState } from 'react';
import { Loader2, LogOut, ShieldCheck, Users } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import Logo from '../../components/Logo';
import Affiliate from '../account/Affiliate';

/**
 * İş ortağı portalı — `/ortak`.
 *
 * Yönetim panelinden bağımsız, iş ortaklarına özel bir giriş ekranı ve salt
 * okunur bir panel. Ortaklara bir süre yönetim paneli hesabı açılıyordu ve o
 * hesap bütün ortakları, yeni hesap ekleme ekranını, Yenilikler listesini ve
 * kullanım kılavuzunu görüyordu. Burada ortak yalnızca kendi verisini görür:
 * veri uçları (`/affiliate/stats`, `/affiliate/bets`) oturumdaki hesabın
 * ortaklık kaydını döndürür, başka bir ortağa giden parametre almaz.
 *
 * Hesap sitenin üye hesabıdır: yönetici **Affiliate Yönetimi → Ortak Ekle**
 * ile oluşturur (hesaba `can_use_affiliate` yetkisi de verilir). Giriş aynı
 * `/auth/login` akışını kullanır; iki adımlı doğrulama açıksa kod burada
 * sorulur (AdminLogin'deki akışın aynısı).
 */
const PartnerLogin = () => {
  const { login, verifyTwoFactor, resendTwoFactor } = useAuth();
  const [form, setForm] = useState({ username: '', password: '' });
  const [twoFactor, setTwoFactor] = useState(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    const result = await login(form.username.trim(), form.password);
    setBusy(false);
    if (result.ok) return;
    if (result.twoFactor) {
      setTwoFactor(result.twoFactor);
      setCode('');
      return;
    }
    setError(result.msg || 'Giriş başarısız');
  };

  const submitCode = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    const result = await verifyTwoFactor(
      twoFactor.challenge, code.trim(), twoFactor.purpose,
    );
    setBusy(false);
    if (!result.ok) setError(result.msg || 'Doğrulama başarısız');
  };

  const resend = async () => {
    setError('');
    setNotice('');
    const result = await resendTwoFactor(twoFactor.challenge, twoFactor.purpose);
    if (result.ok) setNotice('Kod yeniden gönderildi');
    else setError(result.msg || 'Kod gönderilemedi');
  };

  return (
    <div className="pusu-panel w-full max-w-sm p-6">
      <div className="mb-5 flex items-center gap-3">
        <div className="grid h-10 w-10 place-items-center rounded-xl bg-[var(--pusu-gold)]/10 text-[var(--pusu-gold)]">
          <Users size={20} />
        </div>
        <div>
          <h1 className="text-lg font-bold text-white">İş Ortağı Girişi</h1>
          <p className="text-xs text-gray-400">Kendi referans ve komisyon raporlarınız</p>
        </div>
      </div>

      {twoFactor ? (
        <form onSubmit={submitCode} className="space-y-3">
          <p className="text-sm text-gray-400">
            {twoFactor.deliveryTarget
              ? `${twoFactor.deliveryTarget} adresine gönderilen kodu girin.`
              : 'Doğrulama kodunu girin.'}
          </p>
          <input
            value={code}
            onChange={(event) => setCode(event.target.value)}
            className="pusu-input text-center tracking-[0.4em]"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={twoFactor.codeLength || 6}
            aria-label="Doğrulama kodu"
            autoFocus
          />
          <button type="submit" disabled={busy || !code.trim()} className="gold-btn w-full rounded-xl py-3 disabled:opacity-50">
            {busy ? <Loader2 size={16} className="inline animate-spin" /> : 'Doğrula'}
          </button>
          {twoFactor.deliveryChannel !== 'totp' && (
            <button type="button" onClick={resend} className="w-full text-xs text-gray-400 hover:text-white">
              Kodu yeniden gönder
            </button>
          )}
        </form>
      ) : (
        <form onSubmit={submit} className="space-y-3">
          <input
            value={form.username}
            onChange={(event) => setForm({ ...form, username: event.target.value })}
            className="pusu-input"
            placeholder="Kullanıcı adı"
            autoComplete="username"
            aria-label="Kullanıcı adı"
          />
          <input
            type="password"
            value={form.password}
            onChange={(event) => setForm({ ...form, password: event.target.value })}
            className="pusu-input"
            placeholder="Şifre"
            autoComplete="current-password"
            aria-label="Şifre"
          />
          <button
            type="submit"
            disabled={busy || !form.username.trim() || !form.password}
            className="gold-btn w-full rounded-xl py-3 disabled:opacity-50"
          >
            {busy ? <Loader2 size={16} className="inline animate-spin" /> : 'Giriş Yap'}
          </button>
        </form>
      )}
      {error && <p className="mt-3 text-sm text-red-400" role="alert">{error}</p>}
      {notice && <p className="mt-3 text-sm text-emerald-400">{notice}</p>}
    </div>
  );
};

const PartnerPortal = () => {
  const { user, ready, logout } = useAuth();

  if (!ready) {
    return (
      <div className="pusu-body-gradient grid min-h-screen place-items-center text-gray-400">
        <Loader2 className="animate-spin text-[var(--pusu-gold)]" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="pusu-body-gradient flex min-h-screen flex-col items-center justify-center gap-6 px-4 py-10">
        <Logo />
        <PartnerLogin />
      </div>
    );
  }

  const header = (
    <header className="partner-portal-bar mb-5 flex items-center gap-3">
      <Logo />
      <span className="hidden text-sm font-semibold text-gray-400 sm:inline">İş Ortağı Paneli</span>
      <span className="ml-auto truncate text-sm text-gray-300">{user.username}</span>
      <button
        type="button"
        onClick={() => logout()}
        className="side-icon-btn inline-flex"
        aria-label="Çıkış yap"
        title="Çıkış yap"
      >
        <LogOut size={16} />
      </button>
    </header>
  );

  return (
    <div className="pusu-body-gradient min-h-screen">
      <div className="mx-auto max-w-6xl px-3 py-5 sm:px-4 md:py-8">
        {header}
        {user.can_use_affiliate ? (
          <Affiliate readOnly />
        ) : (
          <div className="pusu-panel p-8 text-center text-gray-400">
            <ShieldCheck size={28} className="mx-auto mb-3 text-[var(--pusu-gold)]" />
            Bu hesap bir iş ortağı hesabı değil. İş ortağı hesabınızla giriş
            yapın ya da yöneticinize başvurun.
          </div>
        )}
      </div>
    </div>
  );
};

export default PartnerPortal;
